import { useId } from "react";
import { useFormikContext } from "formik";
import { TaskCreate } from "@common/contracts/task.contract";
import { fullMonthFullYearFormat } from "@common/helpers/date";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import {
    getMonthRange,
    parseDateOnly,
    toDateOnlyRange,
} from "@design-system/DatePicker/DatePicker.lib";
import type { DateOnlyRange } from "@design-system/DatePicker/DatePicker.types";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import styles from "./CreateTaskDrawer.module.scss";
import taskStyles from "./TaskDrawerFields.module.scss";
import { ReportDisplayFields } from "@components/reports/ReportDisplayFields";
import {
    applyReportDisplayPatch,
    isReportTaskType,
    readReportDisplayFilters,
} from "@components/reports/reportDisplayFilters";
import { isPeriodTaskType } from "./taskWorkflow";
import { useWorkflowViewUpdater } from "./useWorkflowViewUpdater";

const toMonthPickerValue = (
    date: string | undefined,
): DateOnlyRange | undefined => {
    const parsedDate = parseDateOnly(date);
    return parsedDate ? toDateOnlyRange(getMonthRange(parsedDate)) : undefined;
};

export const TaskPeriodFields = () => {
    const startLabelId = useId();
    const endLabelId = useId();
    const formik = useFormikContext<TaskCreate>();
    const updateView = useWorkflowViewUpdater();
    const { type, view } = formik.values;

    if (!isPeriodTaskType(type)) {
        return null;
    }

    const periodStart = view?.filters?.periodStart;
    const periodEnd = view?.filters?.periodEnd;

    return (
        <>
            <DescriptionListItem
                label={<span id={startLabelId}>Start month</span>}
            >
                <div className={styles.datePickerContainer}>
                    <DatePicker
                        mode="month"
                        variant="panel-input"
                        className={styles.detailField}
                        availableModes={["month"]}
                        displayFormat={fullMonthFullYearFormat}
                        placeholder="Select month"
                        showTriggerIcon={false}
                        value={toMonthPickerValue(periodStart)}
                        onValueChange={(range) =>
                            updateView({ periodStart: range?.from ?? "" })
                        }
                        fieldControlProps={{ "aria-labelledby": startLabelId }}
                        buttonProps={{
                            className: periodStart
                                ? undefined
                                : styles.emptyValue,
                        }}
                    />
                </div>
            </DescriptionListItem>
            <DescriptionListItem label={<span id={endLabelId}>End month</span>}>
                <div className={styles.datePickerContainer}>
                    <DatePicker
                        mode="month"
                        variant="panel-input"
                        className={styles.detailField}
                        availableModes={["month"]}
                        displayFormat={fullMonthFullYearFormat}
                        placeholder="Select month"
                        showTriggerIcon={false}
                        value={toMonthPickerValue(periodEnd)}
                        onValueChange={(range) =>
                            updateView({
                                periodEnd: range?.to ?? range?.from ?? "",
                            })
                        }
                        fieldControlProps={{ "aria-labelledby": endLabelId }}
                        buttonProps={{
                            className: periodEnd
                                ? undefined
                                : styles.emptyValue,
                        }}
                    />
                </div>
            </DescriptionListItem>
            {isReportTaskType(type) && (
                <ReportDisplayFields
                    taskType={type}
                    value={readReportDisplayFilters(view?.filters)}
                    wrapperClassName={taskStyles.detailSelectWrapper}
                    triggerClassName={taskStyles.detailSelectTrigger}
                    onChange={(patch) => {
                        const {
                            cycle,
                            comparisons,
                            comparisonDiffs,
                            ratioTypes,
                        } = applyReportDisplayPatch({
                            filters: view?.filters,
                            patch,
                        });
                        updateView({
                            cycle,
                            comparisons,
                            comparisonDiffs,
                            ratioTypes,
                        });
                    }}
                />
            )}
        </>
    );
};
